import { useEffect, useRef, useState } from 'react';
import { api, FetchError } from '../api.ts';
import { Spinner } from '../components/Spinner.tsx';
import { BriefImageDropzone } from '../components/BriefImageDropzone.tsx';
import {
  ProspectIntakeSchema,
  type BriefImage,
  type GenerateResponse,
  type Proposal,
  type ProspectIntake,
} from '@sab/core/schemas';

interface IntakeProps {
  onCreated: (proposal: Proposal) => void;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'analyzing' }
  | {
      kind: 'preview';
      analysis: GenerateResponse['analysis'];
      intake: ProspectIntake;
      analyzeDiag: GenerateResponse['diagnostics'];
    }
  | {
      kind: 'generating';
      jobId: string;
      progress: number;
      analysis: GenerateResponse['analysis'];
      intake: ProspectIntake;
      elapsedSec: number;
    }
  | { kind: 'saving' }
  | { kind: 'error'; message: string };

/** Draft schema bumped to v2 — added briefImages array, removed
 * budgetHintIDR from form (auto-decided by tools). v1 drafts auto-migrate
 * to v2 by drop-load. */
const DRAFT_KEY = 'sab.intake.draft.v2';
const DRAFT_VERSION = 2;

interface FormState {
  companyName: string;
  websiteUrl: string;
  contactName: string;
  contactRole: string;
  language: 'id' | 'en';
  rawMessage: string;
  notes: string;
  /** Uploaded brief screenshots (R2 keys). Auto-extract pipeline reads
   * these to prefill the other fields. */
  briefImages: BriefImage[];
  /** Which fields were just auto-filled by the extract step — surfaced
   * in the UI with an orange highlight + "diisi otomatis" badge. */
  autoFilled: Partial<Record<keyof FormState, true>>;
}

const EMPTY_FORM: FormState = {
  companyName: '',
  websiteUrl: '',
  contactName: '',
  contactRole: '',
  language: 'id',
  rawMessage: '',
  notes: '',
  briefImages: [],
  autoFilled: {},
};

function loadDraft(): FormState | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: number } & Partial<FormState>;
    if (parsed.v !== DRAFT_VERSION) return null;
    return {
      companyName: typeof parsed.companyName === 'string' ? parsed.companyName : '',
      websiteUrl: typeof parsed.websiteUrl === 'string' ? parsed.websiteUrl : '',
      contactName: typeof parsed.contactName === 'string' ? parsed.contactName : '',
      contactRole: typeof parsed.contactRole === 'string' ? parsed.contactRole : '',
      language: parsed.language === 'en' ? 'en' : 'id',
      rawMessage: typeof parsed.rawMessage === 'string' ? parsed.rawMessage : '',
      notes: typeof parsed.notes === 'string' ? parsed.notes : '',
      briefImages: Array.isArray(parsed.briefImages) ? parsed.briefImages : [],
      autoFilled: {},
    };
  } catch {
    return null;
  }
}

function saveDraft(state: FormState): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ v: DRAFT_VERSION, ...state }));
  } catch {
    /* quota or disabled — ignore */
  }
}

function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

interface ModelOption {
  id: string;
  label: string;
  tagline: string;
  provider: string;
  tier: string;
  approxCostUSD: number;
  available: boolean;
  secretEnv: string | null;
  getKeyUrl: string | null;
}

export function Intake({ onCreated }: IntakeProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [form, setForm] = useState<FormState>(() => loadDraft() ?? EMPTY_FORM);
  const [restored, setRestored] = useState<boolean>(() => loadDraft() !== null);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [modelId, setModelId] = useState<string>('deepseek-chat');

  useEffect(() => {
    let cancelled = false;
    api
      .models()
      .then((res) => {
        if (cancelled) return;
        setModels(res.models);
        setModelId(res.defaultModelId);
      })
      .catch(() => {
        /* keep deepseek default */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-save form state on every change (debounced)
  const saveTimer = useRef<number | null>(null);
  useEffect(() => {
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      // Don't save totally-empty draft to avoid sticky empty entry
      const anyValue = Object.values(form).some(
        (v) => typeof v === 'string' && v.trim().length > 0,
      );
      if (anyValue) saveDraft(form);
      else clearDraft();
    }, 250);
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [form]);

  // Warn on tab close if user has draft in progress and didn't generate yet
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      const hasContent = Object.values(form).some(
        (v) => typeof v === 'string' && v.trim().length > 0,
      );
      if (phase.kind === 'idle' && hasContent) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [form, phase.kind]);

  const handleResetDraft = () => {
    clearDraft();
    setForm(EMPTY_FORM);
    setRestored(false);
  };

  /* ----- Auto-scroll: saat phase berubah ke preview / generating, scroll
   * ke section baru supaya user langsung lihat hasil analisa / progres
   * tanpa harus scroll manual. Respects prefers-reduced-motion. */
  const previewRef = useRef<HTMLDivElement | null>(null);
  const generatingRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (phase.kind !== 'preview' && phase.kind !== 'generating') return;
    const target = phase.kind === 'preview' ? previewRef.current : generatingRef.current;
    if (!target) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Defer one frame so layout settles after phase transition render
    const id = window.setTimeout(() => {
      target.scrollIntoView({
        behavior: reduced ? 'auto' : 'smooth',
        block: 'start',
      });
    }, 80);
    return () => window.clearTimeout(id);
  }, [phase.kind]);

  /* ----- Auto-extract: vision LLM reads uploaded screenshots and
   * prefills companyName / contactName / contactRole / websiteUrl /
   * rawMessage. Only fills empty fields — never overwrites user input.
   *
   * IMPORTANT: accept keys as argument, jangan baca dari `form.briefImages`
   * (stale closure — setForm async, state belum update saat callback fire). */
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);

  const runAutoExtract = async (allKeys: string[]) => {
    if (allKeys.length === 0) return;
    setExtracting(true);
    setExtractError(null);
    try {
      const res = await api.extractBrief(allKeys);
      const ex = res.extracted;
      // Detect kalau LLM return semua null/empty — tampilkan info friendly
      const anyFilled =
        Boolean(ex.companyName) ||
        Boolean(ex.contactName) ||
        Boolean(ex.contactRole) ||
        Boolean(ex.websiteUrl) ||
        Boolean(ex.rawMessage);
      if (!anyFilled) {
        setExtractError(
          `AI tidak menemukan info yang bisa di-extract dari ${res.imageCount} screenshot ini (model: ${res.modelUsed}). Coba upload screenshot yang lebih jelas atau isi manual.`,
        );
        setTimeout(() => setExtractError(null), 8_000);
        return;
      }
      setForm((f) => {
        const next: FormState = { ...f };
        const filled: Partial<Record<keyof FormState, true>> = { ...f.autoFilled };
        const fillIfEmpty = (
          key: 'companyName' | 'contactName' | 'contactRole' | 'websiteUrl' | 'rawMessage',
          value: string | null | undefined,
        ) => {
          if (!value) return;
          if (next[key] && next[key].trim().length > 0) return; // never overwrite user input
          next[key] = value;
          filled[key] = true;
        };
        fillIfEmpty('companyName', ex.companyName);
        fillIfEmpty('contactName', ex.contactName);
        fillIfEmpty('contactRole', ex.contactRole);
        fillIfEmpty('websiteUrl', ex.websiteUrl);
        fillIfEmpty('rawMessage', ex.rawMessage);
        next.autoFilled = filled;
        return next;
      });
    } catch (err) {
      const msg =
        err instanceof FetchError
          ? `${err.body.error} (kode ${err.body.code}, HTTP ${err.status})`
          : 'Gagal baca screenshot — isi manual saja.';
      setExtractError(msg);
      // Auto-clear after 8s so user can still fill manually
      setTimeout(() => setExtractError(null), 8_000);
    } finally {
      setExtracting(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    const intakeRaw: Record<string, unknown> = {
      companyName: form.companyName.trim(),
      language: form.language,
      geography: 'Indonesia',
    };
    if (form.websiteUrl.trim()) intakeRaw.websiteUrl = form.websiteUrl.trim();
    if (form.contactName.trim()) intakeRaw.contactName = form.contactName.trim();
    if (form.contactRole.trim()) intakeRaw.contactRole = form.contactRole.trim();
    if (form.rawMessage.trim()) intakeRaw.rawMessage = form.rawMessage.trim();
    if (form.notes.trim()) intakeRaw.notes = form.notes.trim();
    if (form.briefImages.length > 0) intakeRaw.briefImages = form.briefImages;

    const parsed = ProspectIntakeSchema.safeParse(intakeRaw);
    if (!parsed.success) {
      setPhase({ kind: 'error', message: parsed.error.errors[0]?.message ?? 'Form tidak valid' });
      return;
    }

    setPhase({ kind: 'analyzing' });
    try {
      const result = await api.analyze({
        intake: parsed.data,
        forceAnalysisRefresh: false,
        modelId,
      });
      setPhase({
        kind: 'preview',
        analysis: result.analysis,
        intake: parsed.data,
        analyzeDiag: result.diagnostics,
      });
    } catch (err) {
      const message = err instanceof FetchError ? err.body.error : 'Gagal menganalisa';
      setPhase({ kind: 'error', message });
    }
  };

  const handleStartDeck = async () => {
    if (phase.kind !== 'preview') return;
    const { intake, analysis } = phase;
    // Move to generating state immediately so UI shows progress; the actual
    // POST happens in a separate effect so React renders the spinner first.
    setPhase({
      kind: 'generating',
      jobId: 'sync',
      progress: 5,
      analysis,
      intake,
      elapsedSec: 0,
    });
  };

  // Drive synthetic progress + run the long deck request when entering 'generating'
  useEffect(() => {
    if (phase.kind !== 'generating') return;
    let cancelled = false;
    const startTs = Date.now();
    const { intake, analysis } = phase;

    // Synthetic progress tick — UX-only, while sync request is in flight.
    const tickId = window.setInterval(() => {
      if (cancelled) return;
      setPhase((p) => {
        if (p.kind !== 'generating') return p;
        const elapsed = Math.round((Date.now() - startTs) / 1000);
        // Model-aware ceiling: faster models reach ~85% at 30s, slower at 90s.
        const model = models.find((m) => m.id === modelId);
        const fast =
          model?.provider === 'deepseek' ||
          model?.provider === 'groq' ||
          model?.provider === 'google';
        const target = fast ? Math.min(92, 5 + elapsed * 1.4) : Math.min(92, 5 + elapsed * 0.5);
        return { ...p, progress: Math.floor(target), elapsedSec: elapsed };
      });
    }, 1200);

    // Kick the deck request — long sync call, ~30-180s depending on model.
    void (async () => {
      try {
        const deck = await api.deck({ intake, analysis, modelId });
        if (cancelled) return;
        setPhase({ kind: 'saving' });
        const { proposal } = await api.create({
          prospect: intake,
          analysis,
          slides: deck.slides,
          theme: { accentHex: '#FF6B35' },
          publishedAt: undefined,
        } as never);
        clearDraft();
        onCreated(proposal);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof FetchError ? err.body.error : 'Pembuatan deck gagal. Coba model lain.';
        setPhase({ kind: 'error', message });
      }
    })();

    return () => {
      cancelled = true;
      window.clearInterval(tickId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind === 'generating' ? 1 : 0]);

  const busy = phase.kind === 'analyzing' || phase.kind === 'saving' || phase.kind === 'generating';

  return (
    <section className="page page--intake" aria-labelledby="intake-title">
      <header className="page__header">
        <div>
          <p className="page__eyebrow">Proposal Baru</p>
          <h1 id="intake-title" className="page__title">
            Briefing Prospect
          </h1>
          <p className="page__lede">
            Isi info prospect. SAB AI akan menganalisa website + bikin draf 25 slide.
          </p>
        </div>
      </header>

      {restored && phase.kind === 'idle' ? (
        <div className="draft-banner" role="status" aria-live="polite">
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="draft-banner__text">
            Draf sebelumnya dipulihkan. Lanjutkan dari tempat Anda berhenti.
          </span>
          <button type="button" className="draft-banner__action" onClick={handleResetDraft}>
            Mulai dari kosong
          </button>
        </div>
      ) : null}

      <form className="form" onSubmit={handleGenerate} noValidate>
        <fieldset disabled={busy}>
          <legend className="visually-hidden">Data prospect</legend>

          {/* Drag-drop entry: upload screenshot conversation / brief
              → auto-extract company name, contact, website, country, etc. */}
          <div className="field">
            <span className="field__label">
              Brief / Screenshot Prospek
              {extracting ? (
                <span className="field__pill field__pill--working">AI sedang baca…</span>
              ) : null}
            </span>
            <BriefImageDropzone
              value={form.briefImages}
              onChange={(briefImages) => setForm((f) => ({ ...f, briefImages }))}
              onUploadsCompleted={({ allImages }) => {
                // Pass keys langsung dari dropzone — bypass stale closure
                void runAutoExtract(allImages.map((img) => img.key));
              }}
              disabled={busy || extracting}
            />
            <p className="field__hint">
              {extracting
                ? 'AI sedang baca screenshot — field di bawah akan otomatis terisi.'
                : extractError
                  ? extractError
                  : 'Drop screenshot WhatsApp/email — AI baca otomatis lalu isi field di bawah. Tidak ada brief? Isi manual saja.'}
            </p>
          </div>

          <div className="field">
            <label htmlFor="companyName" className="field__label">
              Nama Perusahaan{' '}
              <span className="required" aria-hidden="true">
                *
              </span>
              {form.autoFilled.companyName ? (
                <span className="field__pill">diisi otomatis</span>
              ) : null}
            </label>
            <input
              id="companyName"
              className={`field__input ${form.autoFilled.companyName ? 'field__input--autofilled' : ''}`}
              type="text"
              required
              value={form.companyName}
              autoComplete="organization"
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              placeholder="Astra Otoparts"
            />
            <p className="field__hint">Nama legal atau brand yang dikenal prospect.</p>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="websiteUrl" className="field__label">
                Website
              </label>
              <input
                id="websiteUrl"
                className="field__input"
                type="url"
                value={form.websiteUrl}
                inputMode="url"
                autoComplete="url"
                onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
                placeholder="https://astra-otoparts.com"
              />
              <p className="field__hint">SAB AI akan analisa konten website untuk konteks.</p>
            </div>
            <div className="field">
              <label htmlFor="language" className="field__label">
                Bahasa Deck
              </label>
              <select
                id="language"
                className="field__input"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value as 'id' | 'en' })}
              >
                <option value="id">Bahasa Indonesia</option>
                <option value="en">English</option>
              </select>
              <p className="field__hint">Tone & vocab akan menyesuaikan.</p>
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="contactName" className="field__label">
                Nama Kontak <span className="field__optional">(opsional)</span>
                {form.autoFilled.contactName ? (
                  <span className="field__pill">diisi otomatis</span>
                ) : null}
              </label>
              <input
                id="contactName"
                className={`field__input ${form.autoFilled.contactName ? 'field__input--autofilled' : ''}`}
                type="text"
                value={form.contactName}
                autoComplete="name"
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                placeholder="Auto dari screenshot — atau isi manual"
              />
            </div>
            <div className="field">
              <label htmlFor="contactRole" className="field__label">
                Jabatan <span className="field__optional">(opsional)</span>
                {form.autoFilled.contactRole ? (
                  <span className="field__pill">diisi otomatis</span>
                ) : null}
              </label>
              <input
                id="contactRole"
                className={`field__input ${form.autoFilled.contactRole ? 'field__input--autofilled' : ''}`}
                type="text"
                value={form.contactRole}
                onChange={(e) => setForm({ ...form, contactRole: e.target.value })}
                placeholder="Sales Executive"
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="rawMessage" className="field__label">
              Pesan / Brief dari Prospect
            </label>
            <textarea
              id="rawMessage"
              className="field__input field__input--area"
              rows={6}
              value={form.rawMessage}
              onChange={(e) => setForm({ ...form, rawMessage: e.target.value })}
              placeholder="Tempel pesan WhatsApp, email, atau ringkasan call dari prospect..."
            />
            <p className="field__hint">
              Semakin detail, semakin tajam analisa SAB AI — pain points, target customer, urgency.
            </p>
          </div>

          {/* Hint Budget dihapus — tools auto-decide pricing via Stage 4
             country-aware intelligence (lihat permintaanrevisi.md §2 #4). */}
          <div className="field">
            <label htmlFor="notes" className="field__label">
              Catatan Internal <span className="field__optional">(opsional)</span>
            </label>
            <input
              id="notes"
              className="field__input"
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="cth: referensi dari Tico, follow-up minggu depan"
            />
            <p className="field__hint">Tidak ditampilkan di deck — hanya untuk tim SAB.</p>
          </div>

          <div className="field">
            <label htmlFor="modelId" className="field__label">
              Model AI{' '}
              <a href="#/settings" className="field__label-link" tabIndex={-1}>
                Atur kunci
              </a>
            </label>
            <select
              id="modelId"
              className="field__input"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              disabled={busy}
            >
              {models.length === 0 ? (
                <option value="deepseek-chat">DeepSeek V4 Flash · Default</option>
              ) : null}
              {models
                .filter((m) => m.available)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label} · {m.tagline}
                  </option>
                ))}
              {models.some((m) => !m.available) ? (
                <optgroup label="Belum aktif (atur API key dulu di Pengaturan)">
                  {models
                    .filter((m) => !m.available)
                    .map((m) => (
                      <option key={m.id} value={m.id} disabled>
                        🔒 {m.label} — butuh {m.secretEnv ?? 'key'}
                      </option>
                    ))}
                </optgroup>
              ) : null}
            </select>
            <p className="field__hint">
              Default <code>DeepSeek V4 Flash</code> — cepat & murah. Mau coba GPT-5, Claude Opus,
              Gemini, Kimi K2? Buka <a href="#/settings">Pengaturan</a> untuk tambah API key (sekali
              setup).
            </p>
          </div>

          <div className="form__actions">
            <button type="submit" className="cta" disabled={busy || !form.companyName.trim()}>
              {phase.kind === 'analyzing' ? (
                <>
                  <Spinner label="Menyusun" size="sm" />
                  <span>Menyusun draf…</span>
                </>
              ) : (
                <>
                  <span>Susun Draf Proposal</span>
                  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12h14M13 6l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </fieldset>
      </form>

      {phase.kind === 'error' ? (
        <div className="alert alert--error" role="alert">
          {phase.message}
        </div>
      ) : null}

      {phase.kind === 'preview' ? (
        <div className="preview" aria-live="polite" ref={previewRef}>
          <p className="preview__step">Langkah 2 dari 3 · Tinjau Analisa</p>
          <h2 className="preview__title">Analisa siap. Mau lanjut bangun deck?</h2>
          <div className="preview__grid">
            <Pair label="Segment" value={phase.analysis.segment} />
            <Pair label="Tahap" value={phase.analysis.stage} />
            <Pair label="Ukuran" value={phase.analysis.size} />
            <Pair label="Industri" value={phase.analysis.industry} />
            <Pair label="Paket" value={phase.analysis.recommendedPackage} />
            <Pair
              label="Estimasi Harga"
              value={`Rp ${phase.analysis.recommendedPriceIDR.toLocaleString('id-ID')}/bulan`}
            />
          </div>
          <p className="preview__pitch">
            <strong>Pitch angle:</strong> {phase.analysis.pitchAngle}
          </p>
          <p className="preview__rationale">{phase.analysis.rationale}</p>
          <p className="preview__meta">
            Model: {phase.analyzeDiag.model} ·{' '}
            {phase.analyzeDiag.inputTokens + phase.analyzeDiag.outputTokens} token ·{' '}
            {phase.analyzeDiag.latencyMs}ms
          </p>
          <div className="form__actions">
            <button type="button" className="btn" onClick={() => setPhase({ kind: 'idle' })}>
              Edit briefing
            </button>
            <button type="button" className="cta" onClick={handleStartDeck} disabled={busy}>
              <span>Bangun Deck 25 Slide</span>
              <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : null}

      {phase.kind === 'generating' ? (
        <div className="preview deck-progress" aria-live="polite" role="status" ref={generatingRef}>
          <p className="preview__step">Langkah 3 dari 3 · Menyusun Deck</p>
          <h2 className="preview__title">SAB AI sedang menulis 25 slide untuk Anda…</h2>
          <p className="preview__rationale">
            Model premium butuh waktu lebih untuk hasil terbaik. Boleh ditinggal — kami simpan
            progres-nya. Estimasi 40–180 detik tergantung model.
          </p>
          <div className="progress" aria-label="Progress pembuatan deck">
            <div className="progress__track">
              <div
                className="progress__fill"
                style={{ width: `${phase.progress}%` }}
                aria-valuenow={phase.progress}
                aria-valuemin={0}
                aria-valuemax={100}
                role="progressbar"
              />
            </div>
            <div className="progress__meta">
              <span>{phase.progress}%</span>
              <span aria-hidden="true">·</span>
              <span>{phase.elapsedSec}s</span>
              <span aria-hidden="true">·</span>
              <span>{models.find((m) => m.id === modelId)?.label ?? modelId}</span>
            </div>
          </div>
          <ul className="generating-steps" aria-hidden="true">
            <li className={phase.progress >= 15 ? 'is-done' : ''}>Menulis cover & opportunity</li>
            <li className={phase.progress >= 35 ? 'is-done' : ''}>Identifikasi masalah & dampak</li>
            <li className={phase.progress >= 55 ? 'is-done' : ''}>
              Memilih case study yang relevan
            </li>
            <li className={phase.progress >= 75 ? 'is-done' : ''}>
              Membangun strategi & paket harga
            </li>
            <li className={phase.progress >= 95 ? 'is-done' : ''}>Finalisasi timeline & CTA</li>
          </ul>
        </div>
      ) : null}

      {phase.kind === 'saving' ? (
        <div className="preview" aria-live="polite">
          <p className="preview__title">Menyimpan proposal…</p>
          <Spinner label="Menyimpan" size="lg" />
        </div>
      ) : null}
    </section>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div className="pair">
      <span className="pair__label">{label}</span>
      <span className="pair__value">{value}</span>
    </div>
  );
}
